// DME Pipeline — Kanban board: 4 swim lanes grouping 11 order stages.
//
// The first lane also holds referral cards: DME clients who have no open order
// yet. A supplies referral is real work before anyone has typed an order, and
// it used to be invisible here until they did.
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import AdvanceOrderModal from '../../components/dme/AdvanceOrderModal';
import { STATUS_PIPELINE as PIPELINE, SIDE_STATUSES, nextStatus, CATEGORIES, money } from '../../lib/dmeWorkflow';
import { CLOSED_STATUS_FILTER } from '../../lib/serviceModel';

// Four swim lanes are a sequential progression (intake → docs → fulfillment →
// billing), not independent categories — same navy→slate scale as the Home
// Health pipeline zones (Dashboard.js), so the app has one consistent
// "sequential stage" palette instead of a new rainbow per kanban board.
const LANES = [
  {
    key: 'intake',
    label: 'Intake & Auth',
    icon: '🔵',
    color: 'var(--zone-marketing)',
    bg: 'var(--zone-marketing-bg)',
    statuses: ['referral_received', 'auth_pending', 'auth_approved'],
  },
  {
    key: 'docs',
    label: 'Documentation',
    icon: '🟡',
    color: 'var(--zone-processing)',
    bg: 'var(--zone-processing-bg)',
    statuses: ['cmn_pending', 'cmn_complete'],
  },
  {
    key: 'fulfillment',
    label: 'Fulfillment',
    icon: '🚚',
    color: 'var(--zone-approval)',
    bg: 'var(--zone-approval-bg)',
    statuses: ['supplier_ordered', 'shipped', 'delivered', 'pod_confirmed'],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: '💰',
    color: 'var(--zone-closed)',
    bg: 'var(--zone-closed-bg)',
    statuses: ['claim_submitted', 'claim_denied', 'claim_paid'],
  },
];

const STATUS_LABELS = Object.fromEntries([...PIPELINE, ...SIDE_STATUSES].map(p => [p.key, p.label]));

// Product category is a parallel identifier, not a status — neutral for all.
const catColor = () => 'var(--text3)';
const catBg    = () => 'var(--bg2)';

function ModalBackdrop({ onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 480, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

export default function DmePipeline() {
  const navigate = useNavigate();
  const [orders, setOrders]   = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [advancing, setAdvancing] = useState(null);
  const [filterCat, setFilterCat] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data }, { data: dmeClients }] = await Promise.all([
      supabase.from('dme_orders').select('*').eq('is_test', false)
        .neq('status', 'claim_paid')   // hide fully closed unless toggled
        .order('created_at', { ascending: false }),
      supabase.from('clients')
        .select('id, client_id, first_name, last_name, current_status, service_type, created_at')
        .contains('service_lines', ['dme'])
        .not('current_status', 'in', CLOSED_STATUS_FILTER),
    ]);
    const all = data || [];
    setOrders(all);
    // A DME client with no order that is still in flight is a referral card.
    const { data: openAny } = await supabase.from('dme_orders').select('client_id').eq('is_test', false).neq('status', 'claim_paid');
    const withOpen = new Set((openAny || []).map(o => o.client_id));
    setReferrals((dmeClients || []).filter(c => !withOpen.has(c.id)));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const [showPaid, setShowPaid] = useState(false);
  const [allOrders, setAllOrders] = useState([]);

  useEffect(() => {
    if (!showPaid) { setAllOrders(orders); return; }
    (async () => {
      const { data } = await supabase.from('dme_orders').select('*').eq('is_test', false).order('created_at', { ascending: false });
      setAllOrders(data || []);
    })();
  }, [orders, showPaid]);

  const filtered = filterCat ? allOrders.filter(o => o.product_category === filterCat) : allOrders;

  const ordersForLane = (lane) =>
    filtered.filter(o => lane.statuses.includes(o.status));

  const inputStyle = { padding: '7px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>DME Pipeline</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {filtered.length} active order{filtered.length !== 1 ? 's' : ''}{referrals.length ? ` · ${referrals.length} referral${referrals.length !== 1 ? 's' : ''} awaiting an order` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...inputStyle }}>
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={showPaid} onChange={e => setShowPaid(e.target.checked)} />
            Show paid
          </label>
        </div>
      </div>

      {/* Board */}
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12, flex: 1 }}>
        {LANES.map(lane => {
          const laneOrders = ordersForLane(lane);
          return (
            <div
              key={lane.key}
              style={{ background: 'var(--topbar-bg)', border: '0.5px solid var(--border)', borderRadius: 12, minWidth: 250, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              {/* Lane header */}
              <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 14 }}>{lane.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: lane.color }}>{lane.label}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, background: lane.color, color: '#fff', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {loading ? '…' : laneOrders.length + (lane.key === 'intake' ? referrals.length : 0)}
                </span>
              </div>
              <div style={{ height: 2, background: lane.color, opacity: 0.2, margin: '0 10px' }} />

              {/* Sub-stage labels */}
              <div style={{ padding: '6px 10px 4px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {lane.statuses.map(s => (
                  <span key={s} style={{ fontSize: 9, fontWeight: 500, padding: '1px 6px', borderRadius: 10, background: 'var(--bg2)', color: lane.color, border: '0.5px solid var(--border)' }}>
                    {STATUS_LABELS[s]}
                  </span>
                ))}
              </div>

              {/* Cards */}
              <div style={{ padding: '6px 10px 10px', flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 290px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {loading && (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <div className="loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                )}
                {lane.key === 'intake' && !loading && referrals.map(c => (
                  <div key={c.id} style={{ background: 'var(--pipeline-card-bg)', border: '1px dashed var(--border2, var(--border))', borderRadius: 9, padding: '10px 12px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
                      <Link to={`/clients/${c.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{c.last_name}, {c.first_name}</Link>
                    </div>
                    <div style={{ fontSize: 10, color: lane.color, fontWeight: 500, marginBottom: 7, fontFamily: 'var(--mono)' }}>{c.client_id} · referral · no order yet</div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ fontSize: 9, fontWeight: 500, padding: '2px 7px', borderRadius: 20, background: 'var(--bg2)', color: 'var(--text2)', border: '0.5px solid var(--border)', textTransform: 'capitalize' }}>{(c.service_type || '').replace('_', ' ')}</span>
                      <span style={{ fontSize: 9, fontWeight: 500, padding: '2px 7px', borderRadius: 20, background: 'var(--bg2)', color: 'var(--text2)', border: '0.5px solid var(--border)' }}>{c.current_status}</span>
                    </div>
                    <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '4px 10px', width: '100%' }} onClick={() => navigate(`/dme/orders?client=${c.id}`)}>Start order →</button>
                  </div>
                ))}
                {!loading && laneOrders.length === 0 && !(lane.key === 'intake' && referrals.length) && (
                  <div style={{ textAlign: 'center', padding: '24px 10px', color: lane.color, fontSize: 12, opacity: 0.5 }}>No orders</div>
                )}
                {laneOrders.map(order => (
                  <div
                    key={order.id}
                    onClick={() => setSelected(order)}
                    style={{ background: 'var(--pipeline-card-bg)', border: '1px solid var(--border)', borderRadius: 9, padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: 'var(--shadow-pipeline-card)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = lane.color; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    {/* Patient name */}
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{order.client_name}</div>

                    {/* Order ref + HCPCS */}
                    <div style={{ fontSize: 10, color: lane.color, fontWeight: 500, marginBottom: 7, fontFamily: 'var(--mono)' }}>
                      {order.order_ref}{order.hcpcs_code ? ` · ${order.hcpcs_code}` : ''}
                    </div>

                    {/* Category + status badges */}
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: catBg(order.product_category), color: catColor(order.product_category), textTransform: 'capitalize' }}>
                        {order.product_category}
                      </span>
                      <span style={{ fontSize: 9, fontWeight: 500, padding: '2px 7px', borderRadius: 20, background: 'var(--bg2)', color: lane.color, border: '0.5px solid var(--border)' }}>
                        {STATUS_LABELS[order.status]}
                      </span>
                    </div>

                    {order.supplier_name && (
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>📦 {order.supplier_name}</div>
                    )}
                    {order.expected_amount && (
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{money(order.expected_amount)}{order.is_recurring ? ' / month' : ''}</div>
                    )}
                    {order.resupply_blocked_reason && (
                      <div style={{ fontSize: 10, color: 'var(--danger)', marginBottom: 4 }}>⚠ Resupply refused — {order.resupply_blocked_reason}</div>
                    )}

                    {/* Date */}
                    <div style={{ fontSize: 9, color: 'var(--text3)', borderTop: '0.5px solid var(--border)', paddingTop: 5, marginTop: 4 }}>
                      Created {format(new Date(order.created_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {advancing && (
        <AdvanceOrderModal order={advancing} onClose={() => setAdvancing(null)}
          onAdvanced={() => { setSelected(null); load(); }} />
      )}

      {/* Order detail modal */}
      {selected && (() => {
        const lane = LANES.find(l => l.statuses.includes(selected.status)) || LANES[0];
        const next = nextStatus(selected.status);
        return (
          <ModalBackdrop onClose={() => setSelected(null)}>
            <div style={{ padding: '18px 22px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{selected.client_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, fontFamily: 'var(--mono)' }}>{selected.order_ref}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 500, padding: '3px 9px', borderRadius: 20, background: lane.bg, color: lane.color, border: `0.5px solid ${lane.color}`, whiteSpace: 'nowrap' }}>
                  {STATUS_LABELS[selected.status]}
                </span>
                <button style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} onClick={() => setSelected(null)}>✕</button>
              </div>
            </div>

            <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[
                ['Product category', <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: catBg(selected.product_category), color: catColor(selected.product_category), textTransform: 'capitalize' }}>{selected.product_category}</span>],
                ['HCPCS code',       selected.hcpcs_code || '—'],
                ['Physician',        selected.prescribing_physician || '—'],
                ['Qualifying Dx',    selected.qualifying_diagnosis || '—'],
                ['Expected claim',   money(selected.expected_amount)],
                ['Auth #',           selected.auth_number ? `${selected.auth_number} (${selected.auth_start_date || '?'} → ${selected.auth_end_date || '?'})` : '—'],
                ['Prescription',     selected.rx_date ? `${selected.rx_date}${selected.rx_document_url ? ' · on file' : ' · no file'}` : '—'],
                ['Supplier',         selected.supplier_name || '—'],
                ['Tracking #',       selected.tracking_number || '—'],
                ['Shipped',          selected.shipped_date ? format(new Date(selected.shipped_date), 'MMM d, yyyy') : '—'],
                ['Delivered',        selected.delivered_date ? format(new Date(selected.delivered_date), 'MMM d, yyyy') : '—'],
                ['Proof of delivery',selected.pod_received_by ? `${selected.pod_received_by} · ${selected.pod_delivered_date}` : '—'],
                ['Delivery address', selected.delivery_address || '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '0.5px solid var(--border)' }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0, marginRight: 12 }}>{label}</span>
                  <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, textAlign: 'right' }}>{val}</span>
                </div>
              ))}
              {selected.notes && (
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text2)', background: 'var(--bg2)', borderRadius: 7, padding: '8px 10px', lineHeight: 1.5 }}>
                  {selected.notes}
                </div>
              )}
            </div>

            <div style={{ padding: '12px 22px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-outline" onClick={() => setSelected(null)}>Close</button>
              {next && (
                <button className="btn btn-primary" style={{ background: lane.color, borderColor: lane.color }} onClick={() => setAdvancing(selected)}>
                  → {STATUS_LABELS[next]}
                </button>
              )}
              {!next && (
                <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 500, alignSelf: 'center' }}>✓ Order complete</span>
              )}
            </div>
          </ModalBackdrop>
        );
      })()}
    </div>
  );
}
